import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getUserData } from '../../services/API';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { resolveIdleTimeouts } from '../Reader/Phrase/hooks/resolveIdleTimeouts';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { parseSkipTranslationTags } from '../../services/util';
import { playMarkupSequence, splitRepeatVariationOnSplitMarkers } from '../../services/playMarkupSequence';
import { useMmcResponsiveIconButtonProps } from './useMmcResponsiveIconProps';
import { useContainerHeightVar } from '../Formative/containerHeight';
import { MEDIA_TARGETS, POOL_NARRATION_TARGETS, NO_RESPONSE, TriggerActions, isTriggerTarget, scriptShowsTarget, triggerDataShallowEqual } from '../../constants/constants';
import { parseContextScreen } from '../Formative/formativeScreenConstants';
import useOptionMediaPlayback, {
  optionHasMedia,
  optionIsSpoken,
  optionKind,
  optionKinds,
  optionNarratesItself,
  optionsNeedMediaLayout,
} from './useOptionMediaPlayback';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { logSessionInfo } from '../../services/logger';
import MmcPromptBar from './MmcPromptBar';
import MmcMediaSection from './MmcMediaSection';
import MmcOptionsSection from './MmcOptionsSection';
import MmcSubmitButton from './MmcSubmitButton';
import {
  IDLE_ENCOURAGEMENT_TIMEOUT,
  IDLE_SUBMIT_TIMEOUT,
  TIME_BETWEEN_OPTIONS,
  TIME_BETWEEN_SILENT_OPTIONS,
  GRID_OPTION_COUNTS,
  MULTI_SELECT_DELIMITER,
  HIGHLIGHT_DURATION,
  LOG,
  UI_ELEMENTS,
} from './formativeMultipleChoiceConstants';


/**
 * Exact strings passed to speech for readOptionsAloud.
 * Format must match speakWithPromise calls in showTargets [show:options] and playInstructions:
 *   textToSpeak: `"${label}".`
 */
function getReadOptionsAloudPrefetchSegments(optionList) {
  return (optionList || []).map((o) => `"${o.text || o.key}".`);
}

function abortSpeechSettled(speech) {
  return new Promise((resolve) => {
    if (!speech?.abortSpeech) {
      resolve();
      return;
    }
    speech.abortSpeech(resolve);
  });
}

function prefetchReadOptionsAloud(speech, optionList) {
  if (!speech?.prefetch) return;
  const segments = getReadOptionsAloudPrefetchSegments(optionList);
  // readOptionsAloud uses skipTranslation: bilingualMode, so prefetch with same flag for cache alignment
  if (segments.length) speech.prefetch(segments, { skipTranslation: getUserData().bilingualMode });
}

const FormativeMultipleChoice = ({
  speech,
  options,
  videoURL,
  imageURL,
  imageInPrompt,
  questionText,
  readOptionsAloud,
  verticalOptions,
  multiSelect,
  isMarkupInstruction,
  locale,
  isInstruction,
  isRepeating,
  onDone,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
  onImageLoadError,
  onAllImagesFailed,
  idleEncouragementText,
  instructionRepeatable,
  instructionMaxRepeats,
  instructionRepeatVariation,
  repeatInstructions,
  handleRepeatInstructions,
  beginOverlayRepeatPlayback,
  endOverlayRepeatPlayback,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
  deferOptionNarration,
  verbalInstructions,
}) => {
  const [inFTUE, setInFTUE] = useState(false);
  const inFTUERef = useRef(inFTUE);
  inFTUERef.current = inFTUE;
  const containerRef = useRef(null);
  const containerHeightPx = useContainerHeightVar(containerRef, '--mmc-h');
  const effectiveOptions = options || [];

  const hasOptions = effectiveOptions.length > 0;
  const isImageBased = effectiveOptions.some(o => o.imageUrl);
  const isMediaBased = optionsNeedMediaLayout(effectiveOptions);
  const isVerticalText = verticalOptions || false;
  const narratesOptions =
    readOptionsAloud || effectiveOptions.some(optionNarratesItself);
  const optionsCarryMedia = effectiveOptions.some(optionHasMedia);
  const hasVideo = !!videoURL;
  // Vocabulary clue images render inside the prompt bar (the definition slot),
  // not the media section below it. Video (if any) always owns the media section.
  const promptImageURL = imageInPrompt && !hasVideo ? imageURL : null;
  const hasImage = !!imageURL && !promptImageURL;

  // State — visible by default. Only what's provided renders (JSX guards on data).
  // The [start] trigger hides everything for FTUE modeling.
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [playingOption, setPlayingOption] = useState(null);
  /** True only while options are being narrated in order, which is when the spotlight runs. */
  const [narrationPassActive, setNarrationPassActive] = useState(false);
  const [stimulusReplaysUsed, setStimulusReplaysUsed] = useState(0);
  const [hidePrompt, setHidePrompt] = useState(false);
  const [hideMedia, setHideMedia] = useState(false);
  const [hideOptions, setHideOptions] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [hideRepeat, setHideRepeat] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [canSelectOption, setCanSelectOption] = useState(false);
  const [animatedOptions, setAnimatedOptions] = useState([]);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);
  /** Synchronous guard: state submitted can lag; blocks double onDone if handleSubmit runs twice before re-render. */
  const submissionDispatchedRef = useRef(false);
  const [highlightedOption, setHighlightedOption] = useState(null);
  const [pointerTarget, setPointerTarget] = useState(null);
  const [showRepeatOverlay, setShowRepeatOverlay] = useState(false);
  /** After [show:repeatOverlayInput]; false during TTS between overlay visible and input gate. */
  const [repeatOverlayInputEnabled, setRepeatOverlayInputEnabled] = useState(false);
  /** True from Replay click until overlay TTS onEnd/onError — syncs with Reader isRepeating and blocks Next/Replay. */
  const [repeatOverlayPlaybackBusy, setRepeatOverlayPlaybackBusy] = useState(false);

  // A square grid only reads well when the tiles are the whole page. Any stimulus
  // sharing the screen keeps the single-row layout so there is room for both. A stimulus
  // the script has hidden is judged on what the student sees, not on what data exists —
  // otherwise the tiles reflow when the context screen hands off to the item.
  // verticalOptions is an explicit instruction to stack, so it outranks the grid entirely.
  const optionsOwnThePage = hideMedia || (!imageURL && !videoURL);
  const useGridLayout =
    !isVerticalText &&
    optionsOwnThePage &&
    GRID_OPTION_COUNTS.has(effectiveOptions.length);


  const isMountedRef = useIsMounted();
  const prevTriggerRef = useRef(null);
  const prevTriggerDataRef = useRef(null);
  const triggerVideoResolverRef = useRef(null);
  const stimulusReplayOnRepeatInFlightRef = useRef(false);
  const pointerTimeoutRef = useRef(null);
  /** Synchronous storage for playVideo() promise resolve — avoids race if onDone fires before state commit */
  const videoPromiseResolverRef = useRef(null);
  /** Invalidate in-flight FTUE option stagger if hide:options or a new show:options runs */
  const ftueOptionsRevealGenerationRef = useRef(0);
  const ftueOptionsRevealAwaitPendingRef = useRef(false);
  /** Invalidate in-flight playInstructions option stagger / speech if unmount or superseded */
  const playInstructionsGenerationRef = useRef(0);
  /** True while [show:repeatOverlayInput] is blocking Phrase until Next (or hide/unmount cleanup). */
  const repeatOverlayPendingRef = useRef(false);
  /** In-flight Replay tap — blocks double-starts before state commits. */
  const repeatOverlayReplayInFlightRef = useRef(false);
  /** Reader beginOverlayRepeatPlayback ran — must call endOverlayRepeatPlayback in finishReplay or unmount. */
  const overlayRepeatTimersActiveRef = useRef(false);
  /** Bumped on each syllable-replay start so an in-progress playMarkupSequence loop
   *  can bail (stop issuing further tokens) on unmount or a newer replay. */
  const syllableReplayGenerationRef = useRef(0);
  /** Populated (below) once playMarkupSequence is defined, so the idle-nudge path can
   *  play syllables from mp3 even though useIdleTimer is created above that definition. */
  const speakSyllableEncouragementRef = useRef(null);
  const endOverlayRepeatPlaybackRef = useRef(endOverlayRepeatPlayback);
  endOverlayRepeatPlaybackRef.current = endOverlayRepeatPlayback;

  /** Reader passes `setHideRepeatButton.bind(this)` — new identity every render; do not list in effect deps. */
  const setHideParentRepeatButtonRef = useRef(setHideParentRepeatButton);
  setHideParentRepeatButtonRef.current = setHideParentRepeatButton;

  const onTriggerActionCompleteRef = useRef(onTriggerActionComplete);
  onTriggerActionCompleteRef.current = onTriggerActionComplete;

  useEffect(() => {
    submittedRef.current = submitted;
    if (!submitted) {
      submissionDispatchedRef.current = false;
    }
  }, [submitted]);

  const selectedKeysRef = useRef([]);
  const selectionGenerationRef = useRef(0);
  useEffect(() => {
    selectedKeysRef.current = selectedKeys;
  }, [selectedKeys]);
  const hasSelection = selectedKeys.length > 0;

  const effectiveOptionsRef = useRef(effectiveOptions);
  effectiveOptionsRef.current = effectiveOptions;

  // ImageSelection keeps this tally for the layout it owns. The media layout renders its
  // own tiles, so a row where every picture 404s reaches Sentry only if we count here too.
  const failedImageCountRef = useRef(0);
  const allImagesFailedReportedRef = useRef(false);
  useEffect(() => {
    failedImageCountRef.current = 0;
    allImagesFailedReportedRef.current = false;
  }, [effectiveOptions]);

  const handleMediaImageLoadError = useCallback(
    (imageUrl, formattedImageUrl) => {
      onImageLoadError?.(imageUrl, formattedImageUrl);
      failedImageCountRef.current += 1;
      const pictureCount = effectiveOptionsRef.current.filter(
        (option) => optionKind(option) === optionKinds.image
      ).length;
      if (
        !allImagesFailedReportedRef.current
        && pictureCount > 0
        && failedImageCountRef.current >= pictureCount
      ) {
        allImagesFailedReportedRef.current = true;
        onAllImagesFailed?.();
      }
    },
    [onImageLoadError, onAllImagesFailed]
  );

  const {
    play: playOptionMedia,
    beginPass: beginOptionMediaPass,
    endPass: endOptionMediaPass,
    stop: stopOptionMedia,
    registerVideoElement,
  } = useOptionMediaPlayback(speech);

  const speakOptionLabel = useCallback(
    (option) =>
      speech.speakWithPromise({
        textToSpeak: `"${option.text || option.key}".`,
        skipTranslation: getUserData().bilingualMode,
        useGenerative: true,
      }),
    [speech],
  );

  /**
   * An option that carries its own content narrates itself. readOptionsAloud only governs
   * TTS of a plain text label that has no audioText of its own.
   */
  const narrateOption = useCallback(
    async (option) => {
      const narratesItself = optionNarratesItself(option);
      if (!narratesItself && !readOptionsAloud) {
        return;
      }
      setPlayingOption(option.key);
      try {
        if (optionHasMedia(option)) {
          await playOptionMedia(option);
        } else if (optionIsSpoken(option)) {
          await speech.speakWithPromise({
            textToSpeak: option.audioText,
            skipTranslation: getUserData().bilingualMode,
            useGenerative: true,
          });
        } else {
          await speakOptionLabel(option);
        }
      } finally {
        setPlayingOption((current) => (current === option.key ? null : current));
      }
    },
    [playOptionMedia, readOptionsAloud, speakOptionLabel, speech],
  );

  const narrateOptionRef = useRef(narrateOption);
  narrateOptionRef.current = narrateOption;

  /**
   * Narrates the options in order, optionally revealing each tile as its turn comes.
   * `generation` guards against a newer pass (or unmount) leaving this loop running.
   */
  const narrateOptionsInOrder = useCallback(
    async (generation, { revealAsWeGo }) => {
      // Silent tiles have nothing to reveal and nothing to say, so the pass would only
      // burn TIME_BETWEEN_OPTIONS per tile before the student can answer.
      if (!narratesOptions && !revealAsWeGo) {
        return;
      }
      if (readOptionsAloud) {
        prefetchReadOptionsAloud(speech, effectiveOptions);
      }
      const isStale = () =>
        !isMountedRef.current || ftueOptionsRevealGenerationRef.current !== generation;

      const gapBetweenTiles = narratesOptions
        ? TIME_BETWEEN_OPTIONS
        : TIME_BETWEEN_SILENT_OPTIONS;
      // The spotlight lights one tile and rests the others. With nothing to narrate every
      // tile would rest at once, which reads as all of them shrinking together.
      setNarrationPassActive(narratesOptions);
      if (optionsCarryMedia) beginOptionMediaPass(`reveal:${generation}`);
      try {
        for (const option of effectiveOptions) {
          await new Promise((resolve) => {
            setTimeout(() => {
              if (isStale()) {
                resolve();
                return;
              }
              if (revealAsWeGo) {
                setAnimatedOptions((prev) => [...prev, option.key]);
              }
              narrateOptionRef.current(option).finally(resolve);
            }, gapBetweenTiles);
          });
          if (isStale()) {
            break;
          }
        }
      } finally {
        // A stale pass must not clear the flag out from under the pass that superseded it,
        // or the tiles stop dimming mid-narration.
        if (isMountedRef.current && ftueOptionsRevealGenerationRef.current === generation) {
          setNarrationPassActive(false);
        }
        if (optionsCarryMedia) endOptionMediaPass(`reveal:${generation}`);
      }
    },
    [
      beginOptionMediaPass,
      effectiveOptions,
      endOptionMediaPass,
      narratesOptions,
      optionsCarryMedia,
      readOptionsAloud,
      speech,
    ],
  );

  const revealAndNarrate = useCallback(
    (generation) => narrateOptionsInOrder(generation, { revealAsWeGo: true }),
    [narrateOptionsInOrder],
  );
  const revealAndNarrateRef = useRef(revealAndNarrate);
  revealAndNarrateRef.current = revealAndNarrate;

  const narrateRevealedOptions = useCallback(
    (generation) => narrateOptionsInOrder(generation, { revealAsWeGo: false }),
    [narrateOptionsInOrder],
  );
  const narrateRevealedOptionsRef = useRef(narrateRevealedOptions);
  narrateRevealedOptionsRef.current = narrateRevealedOptions;

  // Match Spelling: Reader.getRepeatInstructions() → repeatInstructions (repeatVariation or short prompt); idle uses same copy when meta is flattened.
  const encouragementText = idleEncouragementText || repeatInstructions;
  const timeoutMessage = COPY_STRINGS.lets_move_on;

  // ASSMNT-2005: per-testlet-type idle/full timeouts (idleTimeoutOverrides, resolved in Phrase.js
  // from testlet.type, same for every language); testlet types with no override fall back below.
  const { idleEncouragementTimeout: extendedIdleEncouragementTimeout, idleSubmitTimeout: extendedSubmitTimeout } = resolveIdleTimeouts(
    idleTimeoutOverrides,
    { idleEncouragementTimeout: IDLE_ENCOURAGEMENT_TIMEOUT, idleSubmitTimeout: IDLE_SUBMIT_TIMEOUT },
    shouldExtendTimeout,
    timeoutExtensionMultiplier
  );

  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech,
    extendedIdleEncouragementTimeout,
    extendedSubmitTimeout,
    // Evaluar syllable blending: voice the idle nudge from pre-recorded syllable mp3
    // (via ref — playMarkupSequence is defined below) instead of TTS-ing "ce, ro".
    isMarkupInstruction ? (text) => speakSyllableEncouragementRef.current?.(text) : undefined
  );

  const idleEncouragementPlayedRef = useRef(false);

  useEffect(() => {
    if (idleSpeechPlaying) idleEncouragementPlayedRef.current = true;
  }, [idleSpeechPlaying]);

  const armIdleTimer = useCallback(
    (idleEncouragementText) => {
      if (idleEncouragementPlayedRef.current) return;
      resetIdleTimer(idleEncouragementText);
    },
    [resetIdleTimer]
  );

  const holdIdleTimerForNarration = useCallback(() => {
    if (idleEncouragementPlayedRef.current) return;
    clearIdleTimer();
  }, [clearIdleTimer]);

  const { repeat: repeatIconSize, submit: submitIconSize } = useMmcResponsiveIconButtonProps();

  /** Reader sets isRepeating while repeat audio plays (same as Spelling); block input like instruction phase. */
  const interactionLocked = isInstruction || isRepeating;

  const unmountVideoWhenDone = useMemo(() => getUserData().sessionData.isiPad, []);

  /**
   * maxRepeats caps replays of the display stimulus. It is independent of repeatable,
   * which governs only the prompt-bar repeat of the instructions.
   * Reset comes from the itemId-keyed remount in Phrase, not from an effect here.
   */
  const stimulusReplayBudget =
    instructionMaxRepeats == null ? Infinity : Math.max(0, instructionMaxRepeats);
  const stimulusReplaysRemaining = stimulusReplayBudget - stimulusReplaysUsed;
  // Reader supplies no text when the item is not repeatable, so a tap would be silent.
  const hasRepeatToPlay =
    !!handleRepeatInstructions && String(repeatInstructions || '').trim() !== '';
  const consumeStimulusReplay = useCallback(() => setStimulusReplaysUsed((used) => used + 1), []);

  /**
   * A context screen is a repeat overlay reached before the options are revealed.
   * Its Replay re-runs that screen -- the stimulus and the narration that went with
   * it -- rather than speaking repeatVariation, which belongs to the options screen.
   * The narration is the speech in the script ahead of the overlay, so nothing has
   * to be authored twice.
   */
  const contextScreen = useMemo(
    () => parseContextScreen(verbalInstructions),
    [verbalInstructions]
  );

  /**
   * A script that never shows the stimulus is not an oversight: a context-screen item
   * plays its clip on screen one, so the question screen must not reveal it again.
   * Without this the reset below pops the stimulus onto the screen the moment the
   * trigger script ends, and the option row reflows because it no longer owns the page.
   */
  const scriptRevealsStimulus = useMemo(
    () => scriptShowsTarget(verbalInstructions, MEDIA_TARGETS),
    [verbalInstructions]
  );
  const scriptRevealsStimulusRef = useRef(scriptRevealsStimulus);
  scriptRevealsStimulusRef.current = scriptRevealsStimulus;

  const onContextScreen = !!contextScreen;
  const onContextScreenRef = useRef(onContextScreen);
  onContextScreenRef.current = onContextScreen;
  // Replay is the only thing maxRepeats caps; Next is always available.
  const canReplayContext = onContextScreen && stimulusReplaysRemaining > 0;

  /** The context screen: the stimulus is all there is, so it grows into the room the rest left. */
  const nothingElseOnScreen = hidePrompt && hideOptions && hideSubmit && hideRepeat;
  const stimulusVisible = !hideMedia && (hasImage || hasVideo);

  const imageOnlyLayout =
    nothingElseOnScreen && stimulusVisible && hasImage && !hasVideo;
  const videoOnlyLayout = nothingElseOnScreen && stimulusVisible && hasVideo;
  /** Narration alone. The replay overlay is all the media slot has to hold itself open around. */
  const audioOnlyLayout = nothingElseOnScreen && !stimulusVisible && showRepeatOverlay;

  const repeatVariationTrimmed = String(instructionRepeatVariation || '').trim();
  const suppressVideoReplayOverlay =
    (isInstruction && instructionRepeatable === true && repeatVariationTrimmed !== '') ||
    showRepeatOverlay ||
    repeatOverlayPlaybackBusy;

  // --- Media helpers ---

  const playVideo = useCallback(async () => {
    LOG('playVideo() — creating promise, setting videoPlaying=true');
    const videoPromise = new Promise((resolve) => {
      videoPromiseResolverRef.current = resolve;
    });
    setVideoPlaying(true);
    await videoPromise;
    if (!isMountedRef.current) return;
    LOG('playVideo() — video promise resolved, setting videoPlaying=false');
    setVideoPlaying(false);
    videoPromiseResolverRef.current = null;
  }, []);

  const canReplayStimulusOnRepeat =
    hasVideo && !hideMedia && !onContextScreen && !submitted && stimulusReplaysRemaining > 0;

  const handlePromptRepeat = useCallback(async () => {
    if (stimulusReplayOnRepeatInFlightRef.current) return;
    if (canReplayStimulusOnRepeat) {
      stimulusReplayOnRepeatInFlightRef.current = true;
      consumeStimulusReplay();
      try {
        await playVideo();
      } finally {
        stimulusReplayOnRepeatInFlightRef.current = false;
      }
      if (!isMountedRef.current) return;
      holdIdleTimerForNarration();
    }
    handleRepeatInstructions();
  }, [
    canReplayStimulusOnRepeat,
    consumeStimulusReplay,
    playVideo,
    holdIdleTimerForNarration,
    handleRepeatInstructions,
  ]);

  // --- Trigger handling (action:target system) ---

  const showTargets = useCallback((targets) => {
    const ftueOptionsInBatch = inFTUERef.current && targets.includes('options');
    let pendingSyncComplete = false;
    // When batch is [show:options,submit], submit's mark is skipped on purpose: the async option
    // stagger owns onTriggerActionComplete: submit is not separately awaitable in that case.
    const markPendingSyncComplete = () => {
      if (!ftueOptionsInBatch) {
        pendingSyncComplete = true;
      }
    };

    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(false); break;
        case 'prompt': setHidePrompt(false); setHideRepeat(false); break;
        case 'options':
          setHideOptions(false);
          // Use ref so [show:options] sees FTUE immediately after [start] (state may not have re-rendered yet).
          // deferOptionNarration means a [play:options] later in the script owns the narration,
          // so the tiles all appear at once here and stay silent.
          if (inFTUERef.current && !deferOptionNarration) {
            ftueOptionsRevealGenerationRef.current += 1;
            const gen = ftueOptionsRevealGenerationRef.current;
            setAnimatedOptions([]);
            ftueOptionsRevealAwaitPendingRef.current = true;
            (async () => {
              try {
                await revealAndNarrateRef.current(gen);
              } finally {
                // A superseded pass owns neither the pending flag nor the awaited completion,
                // so releasing from here would hand back the live pass's trigger.
                if (ftueOptionsRevealGenerationRef.current === gen) {
                  ftueOptionsRevealAwaitPendingRef.current = false;
                  // One completion per show batch; skip if [show:repeatOverlayInput] is waiting for Next
                  if (
                    isMountedRef.current &&
                    !repeatOverlayPendingRef.current
                  ) {
                    onTriggerActionCompleteRef.current?.();
                  }
                }
              }
            })();
          } else {
            setAnimatedOptions(effectiveOptions.map((o) => o.key));
            pendingSyncComplete = true;
          }
          break;
        case 'video': setHideMedia(false); break;
        case 'image': setHideMedia(false); break;
        case 'repeatOverlay': {
          const trimmed = String(instructionRepeatVariation || '').trim();
          if (!onContextScreenRef.current && (instructionRepeatable !== true || trimmed === '')) {
            markPendingSyncComplete();
          } else {
            setRepeatOverlayInputEnabled(false);
            setShowRepeatOverlay(true);
            markPendingSyncComplete();
          }
          break;
        }
        case 'repeatOverlayInput': {
          const trimmed = String(instructionRepeatVariation || '').trim();
          if (!onContextScreenRef.current && (instructionRepeatable !== true || trimmed === '')) {
            markPendingSyncComplete();
          } else {
            setPointerTarget(null);
            setShowRepeatOverlay(true);
            setRepeatOverlayInputEnabled(true);
            repeatOverlayPendingRef.current = true;
          }
          break;
        }
        case 'submit': setHideSubmit(false); break;
        default: LOG(`  show: unknown target "${t}"`); break;
      }
    }

    if (pendingSyncComplete && !repeatOverlayPendingRef.current) {
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
    }
  }, [effectiveOptions, deferOptionNarration, instructionRepeatable, instructionRepeatVariation]);

  const hideTargets = useCallback((targets) => {
    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(true); break;
        case 'prompt':
          setHidePrompt(true);
          setHideRepeat(true);
          break;
        case 'options':
          ftueOptionsRevealGenerationRef.current += 1;
          ftueOptionsRevealAwaitPendingRef.current = false;
          stopOptionMedia();
          setHideOptions(true);
          setAnimatedOptions([]);
          setSelectedKeys([]);
          setHighlightedOption(null);
          break;
        case 'video': setHideMedia(true); break;
        case 'image': setHideMedia(true); break;
        case 'repeatOverlay':
          setShowRepeatOverlay(false);
          setRepeatOverlayInputEnabled(false);
          if (repeatOverlayPendingRef.current) {
            repeatOverlayPendingRef.current = false;
            queueMicrotask(() => onTriggerActionCompleteRef.current?.());
          }
          break;
        case 'submit': setHideSubmit(true); break;
        default: LOG(`  hide: unknown target "${t}"`); break;
      }
    }
  }, [stopOptionMedia]);

  // Highlight is non-awaitable: pointer shows immediately, speech continues,
  // pointer stays visible until the next trigger clears it.
  const highlightTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) {
      LOG('  highlight: (empty — clear pointer)');
      setPointerTarget(null);
      setHighlightedOption(null);
      return;
    }

    if (target === 'repeatOverlayReplay' || target === 'repeatOverlayNext') {
      LOG(`  highlight: repeat overlay button "${target}"`);
      setHighlightedOption(null);
      setPointerTarget(target);
      return;
    }

    if (target === 'backspace') {
      LOG('  highlight: backspace — MMC has no backspace; clear pointer (misrouted trigger)');
      setPointerTarget(null);
      setHighlightedOption(null);
      return;
    }

    if (UI_ELEMENTS.has(target)) {
      LOG(`  highlight: pointing at UI element "${target}"`);
      setPointerTarget(target);
    } else {
      LOG(`  highlight: highlighting option "${target}"`);
      setHighlightedOption(target);
    }
  }, []);

  const clickTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) {
      LOG('  click: no target — completing trigger (no-op)');
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      return;
    }

    if (target === 'backspace') {
      LOG('  click: backspace — MMC has no backspace; completing trigger (no-op)');
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      return;
    }

    if (target === 'video') {
      LOG('  click:video — playing video (awaitable)');
      setHideMedia(false);
      setPointerTarget('video');
      setVideoPlaying(true);
      triggerVideoResolverRef.current = () => {
        LOG('  click:video resolver — video ended');
        setVideoPlaying(false);
        setPointerTarget(null);
        triggerVideoResolverRef.current = null;
        onTriggerActionCompleteRef.current?.();
      };
    } else if (UI_ELEMENTS.has(target)) {
      LOG(`  click: pointing at UI element "${target}"`);
      setPointerTarget(target);
      pointerTimeoutRef.current = setTimeout(() => {
        pointerTimeoutRef.current = null;
        if (!isMountedRef.current) return;
        setPointerTarget(null);
        onTriggerActionCompleteRef.current?.();
      }, HIGHLIGHT_DURATION);
    } else {
      // Target is an option key — highlight it with pointer
      LOG(`  click: highlighting option "${target}" with pointer`);
      setHighlightedOption(target);
      setPointerTarget('option');
      pointerTimeoutRef.current = setTimeout(() => {
        pointerTimeoutRef.current = null;
        if (!isMountedRef.current) return;
        setHighlightedOption(null);
        setPointerTarget(null);
        onTriggerActionCompleteRef.current?.();
      }, HIGHLIGHT_DURATION);
    }
  }, []);

  // Evaluar markup-instruction replay (blending + GPC): voice the repeatVariation from
  // pre-recorded audio via the shared playMarkupSequence (services/playMarkupSequence.js),
  // matching the initial play path, instead of sending raw <syllable>/<phoneme> markup to
  // 11Labs (which cannot voice the tags -> silence). isMountedRef/generation become the
  // caller-supplied isStale() check so the same player is reused by Phrase, MMC, and Reader.
  // Content locale (from the story's tags, via the `locale` prop) selects es-mx vs en-us assets.
  const speakMarkupSequence = useCallback((text, gen) => (
    playMarkupSequence(speech, text, {
      isStale: () => !isMountedRef.current || syllableReplayGenerationRef.current !== gen,
      locale,
    })
  ), [speech, locale]);

  // Wire the idle-nudge playback (its ref is declared above, before playMarkupSequence
  // exists) to the same pre-recorded sequence the repeat button uses. Assigning a ref
  // during render mirrors endOverlayRepeatPlaybackRef above; returns the promise so the
  // idle timer awaits the full sequence.
  speakSyllableEncouragementRef.current = (text) => {
    syllableReplayGenerationRef.current += 1;
    const gen = syllableReplayGenerationRef.current;
    return speakMarkupSequence(String(text), gen);
  };

  /**
   * Replay on a context screen re-runs the screen itself: the narration that
   * introduced the stimulus, then the stimulus. For a video that is the clip
   * playing again, which is what the student already expects from the video
   * player's own replay.
   */
  const replayContextScreen = useCallback(async () => {
    if (
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      repeatOverlayPlaybackBusy ||
      isRepeating ||
      stimulusReplaysRemaining <= 0
    ) {
      return;
    }
    repeatOverlayReplayInFlightRef.current = true;
    setRepeatOverlayPlaybackBusy(true);
    setShowRepeatOverlay(false);
    consumeStimulusReplay();
    try {
      const narration = contextScreen?.narration;
      if (narration) {
        await speech.speakWithPromise({
          textToSpeak: narration,
          skipTranslation: getUserData().bilingualMode,
          useGenerative: true,
        });
      }
      if (hasVideo && isMountedRef.current) await playVideo();
    } finally {
      if (isMountedRef.current) {
        repeatOverlayReplayInFlightRef.current = false;
        setRepeatOverlayPlaybackBusy(false);
        if (repeatOverlayPendingRef.current) setShowRepeatOverlay(true);
      }
    }
  }, [
    repeatOverlayInputEnabled,
    repeatOverlayPlaybackBusy,
    isRepeating,
    stimulusReplaysRemaining,
    consumeStimulusReplay,
    contextScreen,
    speech,
    hasVideo,
    playVideo,
  ]);

  const handleRepeatOverlayReplay = useCallback(() => {
    if (onContextScreen) {
      replayContextScreen();
      return;
    }
    const text = String(instructionRepeatVariation || '').trim();
    if (
      !text ||
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      repeatOverlayPlaybackBusy ||
      isRepeating
    ) {
      return;
    }
    repeatOverlayReplayInFlightRef.current = true;
    setRepeatOverlayPlaybackBusy(true);
    setShowRepeatOverlay(false);

    if (beginOverlayRepeatPlayback) {
      if (!beginOverlayRepeatPlayback()) {
        repeatOverlayReplayInFlightRef.current = false;
        setRepeatOverlayPlaybackBusy(false);
        if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
          setShowRepeatOverlay(true);
        }
        return;
      }
      overlayRepeatTimersActiveRef.current = true;
    }

    const finishReplay = () => {
      if (!isMountedRef.current) return;
      repeatOverlayReplayInFlightRef.current = false;
      setRepeatOverlayPlaybackBusy(false);
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlayback?.();
      }
      if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
        setShowRepeatOverlay(true);
      }
    };

    const segments = splitRepeatVariationOnSplitMarkers(text);
    if (segments.length === 0) {
      finishReplay();
      return;
    }

    // Evaluar markup instruction (blending + GPC): play pre-recorded audio for the
    // repeatVariation instead of sending raw <syllable>/<phoneme> markup to TTS (11Labs),
    // matching the initial instruction play. Pass the raw text — playMarkupSequence parses
    // the markup itself (GPC) or falls back to the bare syllable list (blending). Bilingual
    // mode is fine: language-neutral sounds are marked skipTranslation, prose still translates.
    if (isMarkupInstruction) {
      syllableReplayGenerationRef.current += 1;
      const gen = syllableReplayGenerationRef.current;
      speakMarkupSequence(text, gen)
        .then(finishReplay)
        .catch((err) => {
          // Recover overlay state, but surface the failure — a rejected replay
          // means the student heard nothing on tap, which is worth logging.
          logSessionInfo('mmc_syllable_replay_failed', { message: err?.message });
          finishReplay();
        });
      return;
    }

    const sequenceBase = {
      passThroughSpeechTags: true,
      useGenerative: true,
      onEnd: finishReplay,
      onError: finishReplay,
    };

    if (getUserData().bilingualMode) {
      const flat = segments
        .flatMap((seg) => parseSkipTranslationTags(seg))
        .filter((item) => item && String(item.textToSpeak || '').trim());
      if (!flat.length) {
        finishReplay();
        return;
      }
      // Prefetch parsed segments with correct skipTranslation per item for cache alignment
      if (speech.prefetch) {
        flat.forEach((item) => {
          speech.prefetch([item.textToSpeak], { skipTranslation: item.skipTranslation });
        });
      }
      speech.speakSequence({
        textToSpeak: flat,
        passThroughSpeechTags: true,
        onEnd: finishReplay,
        onError: finishReplay,
      });
    } else {
      // Non-bilingual: prefetch all segments (no skipTranslation, uses default locale)
      if (speech.prefetch) {
        speech.prefetch(segments);
      }
      if (segments.length === 1) {
        speech.speak(
          {
            textToSpeak: segments[0],
            passThroughSpeechTags: true,
            useGenerative: true,
            onEnd: finishReplay,
            onError: finishReplay,
          },
          false,
        );
      } else {
        speech.speakSequence({
          ...sequenceBase,
          textToSpeak: segments.map((s) => ({ textToSpeak: s })),
        });
      }
    }
  }, [
    onContextScreen,
    replayContextScreen,
    speech,
    instructionRepeatVariation,
    isInstruction,
    instructionRepeatable,
    isRepeating,
    repeatOverlayPlaybackBusy,
    beginOverlayRepeatPlayback,
    endOverlayRepeatPlayback,
    repeatOverlayInputEnabled,
    isMarkupInstruction,
    // Depend on the memoized speakMarkupSequence (not the stable import): it closes over
    // `locale`, so a content-locale change must rebuild this callback or overlay Replay
    // would resolve assets against a stale locale.
    speakMarkupSequence,
  ]);

  const handleRepeatOverlayNext = useCallback(() => {
    if (
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      isRepeating ||
      repeatOverlayPlaybackBusy
    ) {
      return;
    }
    repeatOverlayPendingRef.current = false;
    setRepeatOverlayInputEnabled(false);
    setShowRepeatOverlay(false);
    onTriggerActionCompleteRef.current?.();
  }, [
    isRepeating,
    repeatOverlayPlaybackBusy,
    repeatOverlayInputEnabled,
  ]);

  /** [play:options] narrates tiles that [show:options] already put on screen. */
  const playTargets = useCallback((targets) => {
    if (!targets.some((target) => isTriggerTarget(POOL_NARRATION_TARGETS, target))) {
      LOG(`  play: unknown target "${targets.join(',')}"`);
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      return;
    }
    ftueOptionsRevealGenerationRef.current += 1;
    const gen = ftueOptionsRevealGenerationRef.current;
    setAnimatedOptions(effectiveOptionsRef.current.map((o) => o.key));
    ftueOptionsRevealAwaitPendingRef.current = true;
    (async () => {
      try {
        await narrateRevealedOptionsRef.current(gen);
      } finally {
        if (ftueOptionsRevealGenerationRef.current === gen) {
          ftueOptionsRevealAwaitPendingRef.current = false;
          if (isMountedRef.current) {
            onTriggerActionCompleteRef.current?.();
          }
        }
      }
    })();
  }, []);

  const handleTrigger = useCallback((action, data) => {
    LOG(`handleTrigger("${action}", [${data.join(',')}])`);

    // Clear pointer from any previous highlight — each trigger resets it.
    // Highlight will re-set it; click sets its own pointer during animation.
    if (pointerTimeoutRef.current) {
      clearTimeout(pointerTimeoutRef.current);
      pointerTimeoutRef.current = null;
    }
    setPointerTarget(null);
    setHighlightedOption(null);

    switch (action) {
      case TriggerActions.Start:
        LOG('  → Start: hiding everything for FTUE');
        clearIdleTimer();
        setHidePrompt(true);
        setHideMedia(true);
        setHideOptions(true);
        setHideSubmit(true);
        setHideRepeat(true);
        setCanSelectOption(false);
        setAnimatedOptions([]);
        inFTUERef.current = true;
        setInFTUE(true);
        setShowRepeatOverlay(false);
        setRepeatOverlayInputEnabled(false);
        repeatOverlayPendingRef.current = false;
        break;
      case TriggerActions.Ready:
        LOG('  → Ready: FTUE complete');
        inFTUERef.current = false;
        setInFTUE(false);
        setShowRepeatOverlay(false);
        setRepeatOverlayInputEnabled(false);
        repeatOverlayPendingRef.current = false;
        break;
      case TriggerActions.Show:
        showTargets(data);
        break;
      case TriggerActions.Hide:
        hideTargets(data);
        break;
      case TriggerActions.Highlight:
        highlightTargets(data);
        break;
      case TriggerActions.Click:
        clickTargets(data);
        break;
      case TriggerActions.Play:
        playTargets(data);
        break;
      default:
        LOG(`  → Unknown trigger action: "${action}"`);
        break;
    }
  }, [showTargets, hideTargets, highlightTargets, clickTargets, playTargets, clearIdleTimer]);

  const handleTriggerRef = useRef(handleTrigger);
  handleTriggerRef.current = handleTrigger;

  useEffect(() => {
    if (!currentTriggerAction) {
      prevTriggerRef.current = null;
      prevTriggerDataRef.current = null;
      return;
    }
    const data = currentTriggerData || [];
    const sameAction = currentTriggerAction === prevTriggerRef.current;
    const sameData = triggerDataShallowEqual(data, prevTriggerDataRef.current);
    if (sameAction && sameData) {
      return;
    }
    LOG(`Trigger effect — action: "${currentTriggerAction}", data: [${data.join(',')}]`);
    prevTriggerRef.current = currentTriggerAction;
    prevTriggerDataRef.current = [...data];
    handleTriggerRef.current(currentTriggerAction, data);
  }, [currentTriggerAction, currentTriggerData]);

  // --- Flow ---

  const startInteraction = useCallback(() => {
    LOG('startInteraction() — canSelectOption=true, starting idle timer');
    setCanSelectOption(true);
    setHideSubmit(false);
    if (selectedKeysRef.current.length > 0) {
      setSubmitEnabled(true);
    }
    armIdleTimer(encouragementText);
  }, [encouragementText, armIdleTimer]);

  const playInstructions = useCallback(async () => {
    playInstructionsGenerationRef.current += 1;
    const gen = playInstructionsGenerationRef.current;
    LOG('playInstructions() — normal item flow', { hasVideo, readOptionsAloud });
    setCanSelectOption(false);
    setSelectedKeys([]);
    setSubmitEnabled(false);
    clearIdleTimer();

    const playStimulus = async () => {
      if (!hasVideo) return true;
      if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
        return false;
      }
      LOG('  Playing video…');
      await playVideo();
      LOG('  Video done');
      if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
        return false;
      }
      setCanSelectOption(false);
      clearIdleTimer();
      return true;
    };

    if (effectiveOptions.length > 0 && narratesOptions) {
      setAnimatedOptions([]);
    }

    if (optionsCarryMedia && !(await playStimulus())) {
      return;
    }

    if (effectiveOptions.length > 0) {
      if (narratesOptions) {
        LOG('  Revealing', effectiveOptions.length, 'options one by one…');
        ftueOptionsRevealGenerationRef.current += 1;
        await revealAndNarrateRef.current(ftueOptionsRevealGenerationRef.current);
        LOG('  Finished reading options');
      } else {
        LOG('  Showing all options immediately');
        if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
          return;
        }
        setAnimatedOptions(effectiveOptions.map(o => o.key));
      }
    }

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
      return;
    }

    if (!optionsCarryMedia && !(await playStimulus())) {
      return;
    }

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
      return;
    }
    LOG('playInstructions() — done, starting interaction');
    startInteraction();
  }, [
    hasVideo,
    readOptionsAloud,
    narratesOptions,
    optionsCarryMedia,
    effectiveOptions,
    speech,
    clearIdleTimer,
    startInteraction,
    playVideo,
  ]);

  // --- Handlers ---

  /**
   * Only a tap that picks an answer the student has not already chosen touches audio: it
   * cuts what is playing and narrates the new one. Tapping what is already chosen leaves
   * the audio alone — single-select drops the tap, multi-select just releases the key.
   */
  const handleOptionSelection = useCallback(
    async (key) => {
      if (isRepeating) {
        return;
      }
      const option = effectiveOptions.find((o) => o.key === key);
      const isReselect = selectedKeysRef.current.includes(key);
      LOG(`handleOptionSelection("${key}")`, {
        multiSelect,
        isReselect,
        hasMedia: optionHasMedia(option),
      });

      if (isReselect) {
        selectionGenerationRef.current += 1;
        holdIdleTimerForNarration();
        if (!multiSelect) {
          setSubmitEnabled(selectedKeysRef.current.length > 0);
          armIdleTimer(encouragementText);
          return;
        }
        const remainingKeys = selectedKeysRef.current.filter((k) => k !== key);
        selectedKeysRef.current = remainingKeys;
        setSelectedKeys(remainingKeys);
        setSubmitEnabled(remainingKeys.length > 0);
        armIdleTimer(encouragementText);
        return;
      }

      selectionGenerationRef.current += 1;
      const generation = selectionGenerationRef.current;

      const mediaPassToken = `selection:${generation}`;
      const tapPlaysMedia = optionHasMedia(option);
      if (tapPlaysMedia) beginOptionMediaPass(mediaPassToken);

      try {
        stopOptionMedia();

        const nextKeys = multiSelect ? [...selectedKeysRef.current, key] : [key];
        selectedKeysRef.current = nextKeys;
        setSelectedKeys(nextKeys);
        holdIdleTimerForNarration();
        setSubmitEnabled(nextKeys.length > 0);

        await abortSpeechSettled(speech);

        if (!isMountedRef.current) return;
        if (submittedRef.current) return;
        if (selectionGenerationRef.current !== generation) return;

        await narrateOptionRef.current(option);

        if (!isMountedRef.current) return;
        if (submittedRef.current) return;
        if (selectionGenerationRef.current !== generation) return;
        LOG('  Selection done — re-enabling input');
        setCanSelectOption(true);
        setSubmitEnabled(selectedKeysRef.current.length > 0);
        armIdleTimer(encouragementText);
      } finally {
        if (tapPlaysMedia) endOptionMediaPass(mediaPassToken);
      }
    },
    [
      beginOptionMediaPass,
      endOptionMediaPass,
      isRepeating,
      multiSelect,
      effectiveOptions,
      stopOptionMedia,
      holdIdleTimerForNarration,
      encouragementText,
      armIdleTimer,
      speech,
    ],
  );

  const handleSubmit = useCallback(() => {
    if (submissionDispatchedRef.current) {
      LOG('handleSubmit() — skipped, submission already dispatched');
      return;
    }
    submissionDispatchedRef.current = true;
    idleEncouragementPlayedRef.current = false;
    const keys = selectedKeysRef.current;
    LOG(`handleSubmit() — selectedKeys=${JSON.stringify(keys)}`);
    clearIdleTimer();
    stopOptionMedia();
    speech?.abortSpeech?.();
    submittedRef.current = true;
    setSubmitted(true);
    setSubmitEnabled(false);
    setCanSelectOption(false);
    const response = keys.length === 0
      ? NO_RESPONSE
      : multiSelect ? keys.join(MULTI_SELECT_DELIMITER) : keys[0];
    if (onDone) onDone(response);
  }, [onDone, clearIdleTimer, stopOptionMedia, multiSelect, speech]);

  const playInstructionsRef = useRef(playInstructions);
  playInstructionsRef.current = playInstructions;
  const startInteractionRef = useRef(startInteraction);
  startInteractionRef.current = startInteraction;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const speechRef = useRef(speech);
  speechRef.current = speech;
  const timeoutMessageRef = useRef(timeoutMessage);
  timeoutMessageRef.current = timeoutMessage;

  // --- Effects ---

  useEffect(() => {
    setHideParentRepeatButtonRef.current?.(true);
    return () => {
      setHideParentRepeatButtonRef.current?.(false);
    };
  }, []);

  // Unmount: unblock awaitable triggers (click:video, show:repeatOverlayInput) — at most one callback;
  // resolve playVideo() promise so playInstructions is not stuck if VideoPlayer never calls onDone.
  useEffect(() => {
    return () => {
      let needComplete = false;
      // The pointer rest owns the completion for [click:...], which is awaitable. Clearing
      // the timer without releasing it leaves Phrase waiting on a callback that can no
      // longer fire.
      if (pointerTimeoutRef.current) {
        clearTimeout(pointerTimeoutRef.current);
        pointerTimeoutRef.current = null;
        needComplete = true;
      }
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlaybackRef.current?.();
      }
      const resolveVideoPromise = videoPromiseResolverRef.current;
      if (resolveVideoPromise) {
        videoPromiseResolverRef.current = null;
        resolveVideoPromise();
      }
      if (triggerVideoResolverRef.current) {
        triggerVideoResolverRef.current = null;
        needComplete = true;
      }
      if (repeatOverlayPendingRef.current) {
        repeatOverlayPendingRef.current = false;
        needComplete = true;
      }
      if (ftueOptionsRevealAwaitPendingRef.current) {
        ftueOptionsRevealAwaitPendingRef.current = false;
        needComplete = true;
      }
      if (needComplete) {
        onTriggerActionCompleteRef.current?.();
      }
    };
  }, []);

  // Idle encouragement speech vs input: selection read from ref — do not list selectedOption in deps (re-ran
  // this effect on every selection change and could set canSelectOption true mid handleOptionSelection TTS).
  useEffect(() => {
    if (idleSpeechPlaying === undefined) return;
    LOG('Idle speech playing changed:', idleSpeechPlaying);
    setCanSelectOption(!idleSpeechPlaying);
    if (idleSpeechPlaying) {
      setSubmitEnabled(false);
    } else if (selectedKeysRef.current.length > 0) {
      setSubmitEnabled(true);
    }
  }, [idleSpeechPlaying]); // eslint-disable-line react-hooks/exhaustive-deps -- selectedOptionRef only; see comment above effect

  // Idle auto-submit: setSubmitted(true) before TTS locks the UI while "let's move on" plays; onDone runs
  // only once — inside handleSubmit() after that promise (not a double-submit). handleSubmit repeats
  // setSubmitted(true) for a single code path. If we unmount before the .then, onDone may not run;
  // accepted edge case unless product wants synchronous onDone.
  useEffect(() => {
    if (!idleTimeoutExpired) return;
    if (isInstruction || isRepeating || inFTUE || submitted) {
      LOG('Idle timeout ignored — instruction/repeat/FTUE or already submitted');
      return;
    }
    LOG('Idle timeout expired — auto-submitting');
    setSubmitted(true);
    setCanSelectOption(false);
    setSubmitEnabled(false);

    speechRef.current
      .speakWithPromise({
        textToSpeak: timeoutMessageRef.current,
      })
      .then(() => {
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      })
      .catch((err) => {
        logSessionInfo('mmc_idle_timeout_speech_failed', { message: err?.message });
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      });
  }, [idleTimeoutExpired, isInstruction, isRepeating, inFTUE, submitted]);

  useEffect(() => {
    LOG(`isInstruction changed: ${isInstruction}`);
    if (!isInstruction) {
      // FTUE: testlet script may have left prompt/media/repeat hidden if it omitted [show:…] for those targets.
      // Reset chrome for the real item (same baseline as non-FTUE) and reveal option tiles — they stay opacity:0
      // until animatedOptions includes their keys.
      if (inFTUERef.current) {
        LOG('  Trigger-driven flow complete — starting interaction');
        inFTUERef.current = false;
        setInFTUE(false);
        setHidePrompt(false);
        // Only a context screen can legitimately withhold the stimulus on the question screen.
        // This case is shared with the Spanish vocabulary/GPC/listening types, whose scripts may
        // run [start] without ever showing media; they must keep the old always-reveal reset.
        if (!onContextScreenRef.current || scriptRevealsStimulusRef.current) setHideMedia(false);
        setHideOptions(false);
        setHideRepeat(false);
        setSelectedKeys([]);
        selectedKeysRef.current = [];
        const opts = effectiveOptionsRef.current;
        if (opts.length > 0) {
          setAnimatedOptions(opts.map((o) => o.key));
        }
        startInteractionRef.current();
      } else {
        setHidePrompt(false);
        setHideMedia(false);
        setHideOptions(false);
        setHideRepeat(false);
        inFTUERef.current = false;
        setInFTUE(false);
        LOG('  Calling playInstructions()');
        playInstructionsRef.current();
      }
    }
  }, [isInstruction]);

  // --- Render ---

  return (
    <div
      ref={containerRef}
      className={cx('formativeMultipleChoice', {
        'formativeMultipleChoice--imageOnly': imageOnlyLayout,
        'formativeMultipleChoice--videoOnly': videoOnlyLayout,
        'formativeMultipleChoice--audioOnly': audioOnlyLayout,
      })}
    >
      <MmcPromptBar
        hidePrompt={hidePrompt}
        hideRepeat={hideRepeat}
        questionText={questionText}
        imageURL={promptImageURL}
        hideImage={hideMedia}
        containerHeightPx={containerHeightPx}
        repeatIconSize={repeatIconSize}
        onRepeatClick={() => {
          if (!interactionLocked && canSelectOption && hasRepeatToPlay) {
            holdIdleTimerForNarration();
            handlePromptRepeat();
          }
        }}
        repeatDisabled={interactionLocked || !canSelectOption || !hasRepeatToPlay}
        showRepeatPointer={pointerTarget === 'repeat'}
      />

      <MmcMediaSection
        hasVideo={hasVideo}
        hasImage={hasImage}
        hideMedia={hideMedia}
        videoURL={videoURL}
        imageURL={imageURL}
        speech={speech}
        videoPlaying={videoPlaying}
        unmountVideoWhenDone={unmountVideoWhenDone}
        suppressVideoReplayOverlay={suppressVideoReplayOverlay}
        showRepeatOverlay={showRepeatOverlay}
        showRepeatOverlayReplay={onContextScreen ? canReplayContext : true}
        pointerTarget={pointerTarget}
        onRepeatOverlayReplay={handleRepeatOverlayReplay}
        onRepeatOverlayNext={handleRepeatOverlayNext}
        onVideoStart={() => {
          LOG('VideoPlayer onStart');
          holdIdleTimerForNarration();
          setCanSelectOption(false);
          setSubmitEnabled(false);
        }}
        onVideoDone={() => {
          if (!isMountedRef.current) return;
          LOG('VideoPlayer onDone — triggerResolver:', !!triggerVideoResolverRef.current, 'promiseResolver:', !!videoPromiseResolverRef.current);
          if (triggerVideoResolverRef.current) {
            triggerVideoResolverRef.current();
            // [click:video] and playInstructions() both use the same VideoPlayer onDone; if playVideo()'s
            // promise is still pending, resolve it so playInstructions does not hang (Bugbot / race).
            const resolvePlayInstructions = videoPromiseResolverRef.current;
            if (resolvePlayInstructions) {
              videoPromiseResolverRef.current = null;
              resolvePlayInstructions();
            }
            return;
          }
          const resolveVideo = videoPromiseResolverRef.current;
          if (resolveVideo) {
            videoPromiseResolverRef.current = null;
            resolveVideo();
          }
          if (submittedRef.current) return;
          setCanSelectOption(true);
          if (selectedKeysRef.current.length > 0) {
            setSubmitEnabled(true);
          }
          armIdleTimer(encouragementText);
        }}
      />

      <MmcOptionsSection
        hasOptions={hasOptions}
        hideOptions={hideOptions}
        isImageBased={isImageBased}
        isMediaBased={isMediaBased}
        useGridLayout={useGridLayout}
        narrationPassActive={narrationPassActive}
        isVerticalText={isVerticalText}
        effectiveOptions={effectiveOptions}
        animatedOptions={animatedOptions}
        selectedKeys={selectedKeys}
        highlightedOption={highlightedOption}
        playingOption={playingOption}
        canSelectOption={canSelectOption}
        isRepeating={isRepeating}
        multiSelect={multiSelect}
        onOptionSelect={handleOptionSelection}
        onImageLoadError={isMediaBased ? handleMediaImageLoadError : onImageLoadError}
        onAllImagesFailed={onAllImagesFailed}
        registerVideoElement={registerVideoElement}
      />

      <MmcSubmitButton
        submitIconSize={submitIconSize}
        hideSubmit={hideSubmit}
        pointerTarget={pointerTarget}
        isInstruction={isInstruction}
        isRepeating={isRepeating}
        hasSelection={hasSelection}
        submitEnabled={submitEnabled}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default FormativeMultipleChoice;
